import { DirectUpload } from "@rails/activestorage";
import { sleep } from "utils";

/**
 * Uploads a file directly to the server using Active Storage's DirectUpload.
 *
 * @param {File} file - The file to be uploaded.
 * @param {string} uploadUrl - The URL to which the file should be uploaded
 * @param {Function} onProgress - A callback function that receives the upload progress percentage (0-100).
 * @param {Function} onRetry - A callback function that is called when a retry attempt is made after a failed upload.
 * @param {number} [maxRetries=3] - The maximum number of retry attempts for failed uploads.
 *
 * @returns {Promise} A promise that resolves with the blob information upon successful upload, or rejects with an error.
 */
export default async function directUpload({ file, uploadUrl, onProgress, onRetry, maxRetries = 3 }) {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await performUpload(file, onProgress, uploadUrl);
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }

      onProgress?.(0); // Reset progress for retry
      onRetry?.(attempt, maxRetries); // Notify about the retry attempt

      await sleep(1000 * attempt); // Exponential backoff: wait longer for each retry
    }
  }
}

function performUpload(file, onProgress, uploadUrl) {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(file, uploadUrl, {
      directUploadWillStoreFileWithXHR: (request) => {
        request.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        });
      },
    });

    upload.create((error, blob) => {
      if (error) reject(error);
      else resolve(blob);
    });
  });
}
